import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { GtmEventService } from '@core/analytics/gtm-events.service';
import { firstValueFrom } from 'rxjs';

const AUTO_CLOSE_MS = 30_000;

@Component({
  selector: 'app-order-created-snackbar',
  templateUrl: './order-created-snackbar.component.html',
  styleUrls: ['./order-created-snackbar.component.scss'],
})
export class OrderCreatedSnackbarComponent implements OnChanges, OnDestroy {
  @Input() notifications: any[] = [];
  @Output() notificationClosed = new EventEmitter<string>();

  private readonly autoCloseTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly notificationsService: NotificationsService,
    private readonly localStorageService: LocalStorageService,
    private readonly _gtmEventService: GtmEventService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notifications'] && this.notifications) {
      this.notifications = [...this.notifications].reverse();
      this.scheduleAutoClose();
    }
  }

  ngOnDestroy(): void {
    this.autoCloseTimers.forEach((timer) => clearTimeout(timer));
    this.autoCloseTimers.clear();
  }

  /** Programa el cierre automático de cada notificación nueva tras 30 s */
  private scheduleAutoClose(): void {
    const currentIds = new Set(this.notifications.map((n) => n?.id).filter(Boolean));

    // Cancelar timers de notificaciones que ya no están en el array
    this.autoCloseTimers.forEach((timer, id) => {
      if (!currentIds.has(id)) {
        clearTimeout(timer);
        this.autoCloseTimers.delete(id);
      }
    });

    // Crear timer solo para notificaciones nuevas (sin timer activo)
    for (const notification of this.notifications) {
      const id = notification?.id;
      if (!id || this.autoCloseTimers.has(id)) continue;

      const timer = setTimeout(() => {
        this.autoCloseTimers.delete(id);
        this.close(notification);
      }, AUTO_CLOSE_MS);

      this.autoCloseTimers.set(id, timer);
    }
  }

  /**
   * Pedido rechazado: mismo criterio que el panel de campana (`orderStatus === REJECTED`).
   * Fallback por título si la API no envía `orderStatus`.
   */
  isRejected(notification: any): boolean {
    if (this.normalizeOrderStatus(notification?.orderStatus) === 'REJECTED') return true;
    const title = (notification?.title ?? '').toLowerCase();
    return title.includes('no logramos');
  }

  /** Texto secundario para rechazados: mensaje de la API o copia por defecto. */
  rejectionSubtitle(notification: any): string {
    const msg = (notification?.message ?? '').trim();
    return msg || 'Puedes revisar el resultado.';
  }

  /**
   * Subtítulo bajo el título: prioriza `message` de la API (como el panel de campana);
   * si no viene, usa la copia por estado alineada con diseño (notifications.pen).
   */
  orderNotificationSubtitle(notification: any): string {
    if (this.isRejected(notification)) {
      return this.rejectionSubtitle(notification);
    }
    const msg = (notification?.message ?? '').trim();
    if (msg) {
      return msg;
    }
    switch (this.normalizeOrderStatus(notification?.orderStatus)) {
      case 'CREATED':
        return 'Revisa el resumen de tu pedido.';
      case 'CONFIRMED':
        return 'Pronto pasará a preparación y despacho.';
      case 'IN_PREPARATION':
        return 'Estamos preparando tu pedido.';
      case 'IN_TRANSIT':
        return 'Tu pedido está en camino.';
      case 'DELIVERED':
        return 'Revisa el detalle final si lo necesitas.';
      default:
        return 'Revisa el resumen de tu pedido.';
    }
  }

  // ── Timeline de estado de pedido ────────────────────────────────────────────

  readonly orderTimelineSteps: ReadonlyArray<{ label: string }> = [
    { label: 'Creado' },
    { label: 'Confirmado' },
    { label: 'En preparación' },
    { label: 'En tránsito' },
    { label: 'Entregado' },
  ];

  private normalizeOrderStatus(raw?: string | null): string | null {
    if (!raw?.trim()) return null;
    const n = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
    if (
      [
        'REJECTED',
        'CREATED',
        'CONFIRMED',
        'IN_PREPARATION',
        'IN_TRANSIT',
        'DELIVERED',
      ].includes(n)
    ) {
      return n;
    }
    return null;
  }

  hasOrderTimeline(notification: any): boolean {
    const s = this.normalizeOrderStatus(notification?.orderStatus);
    return s != null && s !== 'REJECTED';
  }

  private getOrderTimelineCompletedCount(notification: any): number {
    switch (this.normalizeOrderStatus(notification?.orderStatus)) {
      case 'CREATED':         return 1;
      case 'CONFIRMED':       return 2;
      case 'IN_PREPARATION':  return 3;
      case 'IN_TRANSIT':      return 4;
      case 'DELIVERED':       return 5;
      default:                return 0;
    }
  }

  private getOrderTimelineCurrentStepIndex(notification: any): number {
    const n = this.getOrderTimelineCompletedCount(notification);
    return n < 1 ? -1 : Math.min(n - 1, this.orderTimelineSteps.length - 1);
  }

  isOrderTimelineFutureStep(notification: any, stepIndex: number): boolean {
    return stepIndex > this.getOrderTimelineCurrentStepIndex(notification);
  }

  isOrderTimelineConnectorDone(notification: any, lineIndex: number): boolean {
    return this.getOrderTimelineCompletedCount(notification) > lineIndex + 1;
  }

  isOrderTimelineStepLabelActive(notification: any, stepIndex: number): boolean {
    const cur = this.getOrderTimelineCurrentStepIndex(notification);
    return cur >= 0 && stepIndex === cur;
  }

  /** Extrae el orderId del campo url de la notificación */
  private extractOrderId(notification: any): string | null {
    const url: string = notification?.url ?? '';
    if (!url) return null;
    try {
      return new URL(url).searchParams.get('orderId');
    } catch {
      const match = url.match(/[?&]orderId=([^&]+)/);
      return match ? match[1] : null;
    }
  }

  /**
   * Mismo criterio que `show-notifications`: URLs pensadas para cliente → rutas /main para vendedor.
   */
  private getAdjustedNotificationUrl(url: string): string {
    if (!url?.trim()) {
      return url;
    }
    const user = this.localStorageService.getItem('user');
    if (user?.type !== 'Vendedor') {
      return url;
    }
    return url
      .replace('/tienda/pedido-realizado', '/main/checkout')
      .replace('/cuenta/historial/detalle', '/main/historial-compras/detalle-historial');
  }

  getOrderCode(notification: any): string {
    return this.extractOrderId(notification) ?? 'N/A';
  }

  getTagLabel(): string {
    return 'Pedidos';
  }

  /** Formatea la fecha desde el campo startAt: "DD/MM/YYYY HH:mm" → "DD/MM/YYYY" */
  getFormattedDate(notification: any): string {
    const raw: string = notification?.startAt ?? '';
    if (!raw) return '';
    return raw.split(' ')[0] ?? raw;
  }

  async close(notification: any): Promise<void> {
    if (notification?.id) {
      try {
        await firstValueFrom(
         
          this.notificationsService.markNotificationAsAutoDismissed(notification.id)
        );
      } catch (error) {
        console.error('Error al marcar notificación como notificada:', error);
      }
    }

    if (notification?.id) {
      this.notificationClosed.emit(notification.id);
    }
  }

  /**
   * Misma lógica que `markAsReadAndOpenDetail` en campana: abre `notification.url`
   * con el ajuste para vendedor (`getAdjustedNotificationUrl`), en nueva pestaña.
   */
  async goToOrder(notification: any): Promise<void> {
    this._gtmEventService.clickElementEvent({
      cta: 'Ver detalles',
      seccion: 'Notificaciones',
    });
    const url = (notification?.url ?? '').trim();
    if (!url) {
      await this.close(notification);
      return;
    }

    await this.close(notification);
    window.open(this.getAdjustedNotificationUrl(url), '_blank', 'noopener');
  }
}
